import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import { Pressable, Slider } from '../../Common'
import type { SandfallHistoryPoint, SandfallWindowDays } from './useSandfallHistory'

const WINDOWS: SandfallWindowDays[] = [7, 30, 90]

const formatDayLabel = (day: string): string => {
  const date = new Date(`${day}T00:00:00.000Z`)
  return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(date)
}

const remainingRatio = (point: SandfallHistoryPoint): number => {
  const total = point.columns.reduce((sum, column) => sum + column.totalWeight, 0)
  if (total <= 0) return 0
  const remaining = point.columns.reduce((sum, column) => sum + column.remainingWeight, 0)
  return Math.max(0.04, Math.min(1, remaining / total))
}

export const SandfallScrubber = ({
  points,
  selectedIndex,
  windowDays,
  isLoading,
  hasActivity,
  onSelectedIndexChange,
  onWindowDaysChange,
}: {
  points: SandfallHistoryPoint[]
  selectedIndex: number
  windowDays: SandfallWindowDays
  isLoading: boolean
  hasActivity: boolean
  onSelectedIndexChange: (index: number) => void
  onWindowDaysChange: (days: SandfallWindowDays) => void
}) => {
  if (!hasActivity && !isLoading) {
    return (
      <div className="sandfallScrubber sandfallScrubber--empty">
        <HistoryRoundedIcon fontSize="small" />
        <span>Historical sandfall appears after checklist activity is recorded.</span>
      </div>
    )
  }

  const selectedPoint = points[selectedIndex] ?? points[points.length - 1]

  return (
    <section className="sandfallScrubber" aria-label="Sandfall history">
      <div className="sandfallScrubber__header">
        <span className="sandfallScrubber__title">
          <HistoryRoundedIcon fontSize="inherit" />
          {isLoading ? 'Loading history' : selectedPoint ? formatDayLabel(selectedPoint.day) : 'History'}
        </span>
        <div className="sandfallScrubber__windows" role="group" aria-label="Sandfall history window">
          {WINDOWS.map((days) => (
            <Pressable
              key={days}
              className={windowDays === days ? 'isActive' : ''}
              onClick={() => onWindowDaysChange(days)}
              aria-pressed={windowDays === days}
            >
              <CalendarMonthRoundedIcon fontSize="inherit" />
              {days}d
            </Pressable>
          ))}
        </div>
      </div>
      <div className="sandfallScrubber__track">
        <div className="sandfallScrubber__bars" aria-hidden="true">
          {points.map((point) => (
            <span
              key={point.day}
              style={{ height: `${Math.round(remainingRatio(point) * 100)}%` }}
            />
          ))}
        </div>
        <Slider
          min={0}
          max={Math.max(0, points.length - 1)}
          value={Math.min(selectedIndex, Math.max(0, points.length - 1))}
          onChange={(next) => onSelectedIndexChange(next)}
          aria-label="Rewind sandfall history"
        />
      </div>
    </section>
  )
}
